import { FC, MouseEvent, useEffect, useMemo, useState } from 'react'
import { FormProvider, useFieldArray, useForm } from 'react-hook-form'
import { useTranslation } from 'react-i18next'
import isEmpty from 'lodash/isEmpty'
import isNumber from 'lodash/isNumber'
import { FormInput } from '@microservices/wiskey-react-components'
import { Check as CheckIcon, Clear as ClearIcon } from '@mui/icons-material'
import {
  Accordion,
  AccordionDetails,
  AccordionSummary,
  Grid,
  IconButton,
  SxProps,
  Theme,
  Typography,
} from '@mui/material'
import { GridExpandMoreIcon } from '@mui/x-data-grid'

import { ScriptValueDialog } from '@microservices/wiskey-react-components'

import { useFetchObjectByCodeQuery } from '@redux/api'
import { useFetchCommandParametersQuery } from '@redux/api/command.api'

import {
  FIELD_BIND_TYPE,
  FIELD_BIND_TYPE_ENUM,
  GENERATOR_INPUT_TYPE,
  MAX_INPUT_LENGTH,
  TYPES_SOURCE_OPTIONS,
} from '@constants'
import { AutocompleteOption, CommandParametersType } from '@types'
import { Variable, FieldInfo } from '@microservices/wiskey-react-components'

import { ParameterList } from './components/ParameterList'
import { ModalRefPathPicker } from '@library'
import { EmbeddedObject } from '@libraryComponents/ModalRefPathPicker'

type CommandParametersProps = {
  parametersMode?: 'params' | 'body'
  sourceCode: string
  sourceTitle: string
  typeSource: TYPES_SOURCE_OPTIONS | undefined
  // fieldsObjectCode опциональный, нужен для перекрытия дефолтного sourceCode в рефах
  fieldsObjectCode?: string
  fieldsObjectTitle?: string
  commandCode: string
  parameters: CommandParametersType[]
  onChange: (parameters: CommandParametersType[]) => void
  defaultExpanded?: boolean
  label?: string
  disableFieldType?: boolean
  labelWidth?: number | string
  containerSx?: SxProps<Theme>
  canChangeRefObject?: boolean
  refObjectOptions?: AutocompleteOption[]
  scriptVariables?: Variable[]
  scriptFieldsInfo?: FieldInfo[]
}

type CommandParametersForm = {
  title: AutocompleteOption | null
  type: AutocompleteOption | null
  objectCode: AutocompleteOption<string> | null
  value: string
  pathArray: EmbeddedObject[] | null
  parameters: CommandParametersType[]
}

export const FIELD_BIND_VALUE_TYPE_OPTIONS = [
  { id: FIELD_BIND_TYPE_ENUM.FIELD, label: FIELD_BIND_TYPE.FIELD },
  { id: FIELD_BIND_TYPE_ENUM.STATIC, label: FIELD_BIND_TYPE.STATIC },
  { id: FIELD_BIND_TYPE_ENUM.JS, label: FIELD_BIND_TYPE.JS },
]

export const COMMAND_PARAMETER_TYPES_OPTIONS_WITHOUT_FIELD = [
  { id: FIELD_BIND_TYPE_ENUM.STATIC, label: FIELD_BIND_TYPE.STATIC },
  { id: FIELD_BIND_TYPE_ENUM.JS, label: FIELD_BIND_TYPE.JS },
]

const defaultValues = {
  title: null,
  type: null,
  value: '',
  objectCode: null,
  pathArray: null,
  parameters: [],
}

export const CommandParameters: FC<CommandParametersProps> = ({
  parametersMode = 'params',
  typeSource = TYPES_SOURCE_OPTIONS.OBJECT,
  sourceCode,
  sourceTitle,
  fieldsObjectCode,
  fieldsObjectTitle = '',
  commandCode,
  parameters,
  disableFieldType,
  onChange,
  defaultExpanded = true,
  label,
  labelWidth = 150,
  containerSx = {},
  canChangeRefObject = false,
  refObjectOptions = [],
  scriptVariables,
  scriptFieldsInfo,
}: CommandParametersProps) => {
  const { t } = useTranslation()

  const [isShowScriptValueDialog, setShowScriptValueDialog] = useState(false)
  const [editItemIndex, setEditItemIndex] = useState<number | null>(null)
  const [currentNameScriptDialog, setCurrentNameScriptDialog] = useState<
    'value' | `parameters.${number}.value` | null
  >(null)
  const [addItemObject, setAddItemObject] = useState<AutocompleteOption<string> | null>(null)

  const methods = useForm<CommandParametersForm>({ defaultValues })
  const { control, watch, setValue, getValues, reset } = methods
  const { append, update, remove } = useFieldArray({
    name: 'parameters',
    control,
  })
  const watchTitle = watch('title')
  const watchType = watch('type')
  const watchObjectCode = watch('objectCode')
  const watchValue = watch('value')
  const watchPathArray = watch('pathArray')

  const widthContent = label ? `calc(100% - ${labelWidth}px)` : '100%'
  const expandedAccordion = defaultExpanded || !isEmpty(parameters)

  const { data: commandParameters, isLoading: isLoadingCommandParameters } =
    useFetchCommandParametersQuery(
      {
        objectCode: sourceCode,
        commandCode,
        typeSource: typeSource.toLowerCase(),
      },
      {
        skip: !sourceCode || !commandCode,
      }
    )

  const { data: objectOrSystemModel, isLoading: isLoadingObjectOrSystemModel } =
    useFetchObjectByCodeQuery(commandParameters?.body, {
      skip: !commandCode || parametersMode === 'params' || !commandParameters?.body,
    })

  const isLoading = isLoadingCommandParameters || isLoadingObjectOrSystemModel

  const commandParameterOptions: AutocompleteOption[] = useMemo(() => {
    const usedParams = parameters.map(param => param.title?.id)
    const options =
      parametersMode === 'params' ? commandParameters?.params : objectOrSystemModel?.fields

    if (!options || !sourceCode || !commandCode) return []

    return options
      .filter(param => !usedParams.includes(param.name))
      .map(param => ({ id: param.name, label: param.name }))
  }, [
    parameters,
    typeSource,
    commandParameters?.params,
    objectOrSystemModel?.fields,
    parametersMode,
    sourceCode,
    commandCode,
  ])

  // переменная с опцией объекта для филдов из параметров/боди
  const defaultRefFieldsObjectOption = useMemo(() => {
    // object берется из выбранного значения заданных в refObjectOptions
    if (refObjectOptions.length && addItemObject) {
      return {
        id: addItemObject.id,
        label: addItemObject.label,
      }
    }

    // object берется из props
    if (fieldsObjectCode) {
      return {
        id: fieldsObjectCode,
        label: fieldsObjectTitle || fieldsObjectCode,
      }
    }

    // используется object из entity выше
    if (sourceCode) {
      return {
        id: sourceCode,
        label: sourceTitle || sourceCode,
      }
    }

    return null
  }, [addItemObject])

  const handleSaveScriptDialog = (value: string) => {
    if (currentNameScriptDialog) {
      setValue(currentNameScriptDialog, value, { shouldDirty: true })
    }
  }

  const handleCloseScriptDialog = () => {
    setShowScriptValueDialog(false)
    setCurrentNameScriptDialog(null)
  }

  const handleChangeType = (value: AutocompleteOption, index?: number) => {
    if (isNumber(index)) {
      setValue(`parameters.${index}.value`, '')
      setValue(`parameters.${index}.pathArray`, null)

      return
    }

    setValue('value', '')
    setValue('pathArray', null)
  }

  const handleChangeObjectCode = (value: AutocompleteOption<string>, index?: number) => {
    if (isNumber(index)) {
      setValue(`parameters.${index}.objectCode`, value)

      return
    }

    setValue('objectCode', value)
    setAddItemObject(value)
  }

  const handleClear = (event?: MouseEvent<HTMLButtonElement>, index?: number) => {
    if (isNumber(index)) {
      setValue(`parameters.${index}.title`, parameters[index].title)
      setValue(`parameters.${index}.type`, parameters[index].type)
      setValue(`parameters.${index}.value`, parameters[index].value)
      setValue(`parameters.${index}.pathArray`, parameters[index].pathArray)

      return
    }

    setValue('title', null)
    setValue('type', null)
    setValue('value', '')
    setValue('pathArray', [])

    setAddItemObject(null)
  }

  const handleAdd = () => {
    append({
      id: null,
      title: watchTitle ?? null,
      type: watchType ?? null,
      objectCode: watchObjectCode ?? null,
      value: watchValue,
      pathArray: watchPathArray,
    })
    handleClear()

    onChange(getValues().parameters)
  }

  const handleDelete = (index: number) => {
    remove(index)
    onChange(getValues().parameters)
  }

  const handleUpdate = (index: number) => {
    update(index, getValues().parameters[index])
    onChange(getValues().parameters)
  }

  useEffect(() => {
    reset({
      ...defaultValues,
      parameters,
    })
  }, [parameters])

  return (
    <Grid
      container
      sx={{
        mb: 3,
        ...containerSx,
      }}
    >
      {isShowScriptValueDialog && (
        <ScriptValueDialog
          isShow={isShowScriptValueDialog}
          title={t('tooltips.title.editJs')}
          value={currentNameScriptDialog ? watch(currentNameScriptDialog) : ''}
          onClose={handleCloseScriptDialog}
          onSave={handleSaveScriptDialog}
          language='js'
          variables={scriptVariables}
          fieldsInfo={scriptFieldsInfo}
          width={800}
        />
      )}
      <FormProvider {...methods}>
        {label && (
          <Grid
            item
            alignItems='center'
            flexDirection='row'
            minWidth={labelWidth}
            width={labelWidth}
          >
            <Typography color='#616161' paddingY={1.5} variant='body1' width={labelWidth}>
              {label}
            </Typography>
          </Grid>
        )}
        <Grid item display='flex' flexGrow='1' width={widthContent}>
          <Accordion
            defaultExpanded={expandedAccordion}
            sx={{
              width: '100%',
              '&.MuiPaper-root': {
                // borderRadius: 0,
                border: '1px solid #00000042',
                boxShadow: 'none',
              },
            }}
          >
            <AccordionSummary expandIcon={<GridExpandMoreIcon />}>
              {t(`commandParameters.title.${parametersMode}`)}
            </AccordionSummary>
            <AccordionDetails sx={{ pt: 0, pr: 1 }}>
              <Grid
                container
                border='1px solid #fff'
                flexWrap='nowrap'
                sx={{ overflow: 'auto', scrollbarGutter: 'stable' }}
                xs={12}
              >
                <Grid item xs={3.5}>
                  <FormInput
                    autocompleteOptions={commandParameterOptions}
                    disableClearable={true}
                    inputType={GENERATOR_INPUT_TYPE.AUTOCOMPLETE}
                    label={t('commandParameters.inputs.parameter.label')}
                    labelSx={{ minWidth: 100, width: 100 }}
                    loading={isLoading}
                    name='title'
                    placeholder={t('commandParameters.inputs.parameter.placeholder')}
                    renderAutocompleteOption={(props, option) => (
                      <li {...props} key={option.id} title={option.label}>
                        {option.label}
                      </li>
                    )}
                  />
                </Grid>
                <Grid item xs={2.5}>
                  <FormInput
                    disableClearable={true}
                    disabled={!commandCode}
                    inputType={GENERATOR_INPUT_TYPE.AUTOCOMPLETE}
                    label={t('commandParameters.inputs.type.label')}
                    labelSx={{ minWidth: 60, width: 60, ml: 1 }}
                    name='type'
                    placeholder={t('commandParameters.inputs.type.placeholder')}
                    autocompleteOptions={
                      disableFieldType
                        ? COMMAND_PARAMETER_TYPES_OPTIONS_WITHOUT_FIELD
                        : FIELD_BIND_VALUE_TYPE_OPTIONS
                    }
                    onChangeAutocomplete={handleChangeType}
                  />
                </Grid>
                <Grid item xs={4.5}>
                  {watchType?.id !== FIELD_BIND_TYPE_ENUM.FIELD ? (
                    <FormInput
                      disableClearable={true}
                      disabled={!watchType}
                      inputType={GENERATOR_INPUT_TYPE.INPUT}
                      label={t('commandParameters.inputs.value.label')}
                      labelSx={{ minWidth: 60, width: 60, ml: 1 }}
                      name='value'
                      placeholder={t('commandParameters.inputs.value.placeholder')}
                      readOnly={watchType?.id === FIELD_BIND_TYPE_ENUM.JS}
                      additionalBtn={{
                        isEnabled: watchType?.id === FIELD_BIND_TYPE_ENUM.JS,
                        text: 'edit',
                        color: 'primary',
                        variant: 'contained',
                        onClick: () => {
                          setShowScriptValueDialog(true)
                          setCurrentNameScriptDialog('value')
                        },
                      }}
                      autocompleteOptions={
                        disableFieldType
                          ? COMMAND_PARAMETER_TYPES_OPTIONS_WITHOUT_FIELD
                          : FIELD_BIND_VALUE_TYPE_OPTIONS
                      }
                      formInputContainerSx={{
                        flexWrap: 'nowrap',
                        '& .MuiButton-root': {
                          ml: 0.5,
                        },
                      }}
                      rules={{
                        maxLength: MAX_INPUT_LENGTH,
                      }}
                      onChangeAutocomplete={handleChangeType}
                    />
                  ) : (
                    <ModalRefPathPicker
                      hasClearInInput
                      embeddedObjectPickerControllerName='pathArray'
                      isDisabled={false}
                      label={t('commandParameters.inputs.value.label')}
                      name='value'
                      object={defaultRefFieldsObjectOption}
                      pickerName='value'
                      refObjectOptions={refObjectOptions}
                      refPickAllowed={true}
                      sx={{
                        ml: 1,
                        '& .MuiTypography-root': {
                          width: 60,
                          minWidth: 60,
                        },
                        '& .MuiButton-root': {
                          margin: 0,
                          width: 64,
                        },
                      }}
                      onChangeRootObject={canChangeRefObject ? handleChangeObjectCode : undefined}
                    />
                  )}
                </Grid>
                <Grid item sx={{ display: 'flex', justifyContent: 'center' }} xs={1.5}>
                  <IconButton
                    disabled={!watchTitle || !watchType || !watchValue}
                    size='medium'
                    sx={{ height: 40 }}
                    onClick={handleAdd}
                  >
                    <CheckIcon />
                  </IconButton>
                  <IconButton
                    color='error'
                    disabled={!watchTitle && !watchType && !watchValue}
                    size='medium'
                    sx={{ height: 40 }}
                    onClick={handleClear}
                  >
                    <ClearIcon />
                  </IconButton>
                </Grid>
              </Grid>
              <Grid container>
                <ParameterList
                  canChangeRefObject={canChangeRefObject}
                  commandParameterOptions={commandParameterOptions}
                  defaultFieldsObjectOption={defaultRefFieldsObjectOption}
                  editItemIndex={editItemIndex}
                  isLoadingCommandParameters={isLoading}
                  list={parameters}
                  refObjectOptions={refObjectOptions}
                  onChangeObjectCode={handleChangeObjectCode}
                  onChangeType={handleChangeType}
                  onClear={handleClear}
                  onDelete={handleDelete}
                  onEdit={index => setEditItemIndex(index)}
                  onSetCurrentNameScriptDialog={value => setCurrentNameScriptDialog(value)}
                  onShowScriptValueDialog={() => setShowScriptValueDialog(true)}
                  onUpdate={handleUpdate}
                />
              </Grid>
            </AccordionDetails>
          </Accordion>
        </Grid>
      </FormProvider>
    </Grid>
  )
}
